/**
 * Phase 46 (Plan 46-07) — Right-anchored slide-over drawer for the admin
 * notification dispatch audit page.
 *
 * Mirrors `frontend/src/components/kanban/TaskDetailSlideOver.tsx` (Phase
 * 41-17) VERBATIM for:
 *   - Focus trap (FOCUSABLE_SELECTOR + Tab/Shift+Tab cycle)
 *   - Escape key closes (document-level keydown listener)
 *   - Body scroll lock on open (document.body.style.overflow = 'hidden')
 *   - Backdrop click closes
 *   - Restore focus to the previously focused element on close
 *
 * The drawer is READ-ONLY (no edit/delete modes — the audit log is immutable
 * by design; the dispatcher writes the row and never updates it).
 */
import {
  useEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon } from '../ui/icons';
import { Btn } from '../ui/Btn';
import { Spinner } from '../common/Spinner';
import { StatusPill } from './StatusPill';
import { ChannelLabel } from './ChannelLabel';
import { EventKindLabel } from './EventKindLabel';
import { useQuery } from '@tanstack/react-query';
import { fetchNotificationDispatchAuditById } from '../../services/api/notificationAudit';
import { formatSkipReason } from '../../constants/notificationEnums';
import { formatDisplayDateTime } from '../../utils/format';

// Selector matching all focusable descendants for the focus-trap loop.
// Mirrors TaskDetailSlideOver.tsx line 121-122 verbatim.
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface NotificationAuditDrawerProps {
  open: boolean;
  auditRowId: number | null;
  onClose: () => void;
}

export function NotificationAuditDrawer({
  open,
  auditRowId,
  onClose,
}: NotificationAuditDrawerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  const detailQuery = useQuery({
    queryKey: ['notificationDispatchAudit', 'detail', auditRowId],
    queryFn: () => fetchNotificationDispatchAuditById(auditRowId!),
    enabled: open && auditRowId !== null,
  });

  // Focus management (mirror TaskDetailSlideOver useEffect lines 204-223).
  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    const raf = window.requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      const first = focusables[0];
      if (first) first.focus();
    });

    return () => {
      window.cancelAnimationFrame(raf);
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [open]);

  // Document-level Escape listener (mirror TaskDetailSlideOver lines 227-243).
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Body scroll lock (mirror TaskDetailSlideOver lines 269-276).
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Tab-key trap (mirror TaskDetailSlideOver lines 247-266).
  function handlePanelKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusables = Array.from(
      panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => !el.hasAttribute('disabled'));
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  if (!open) return null;

  const detail = detailQuery.data;
  const isLoading = detailQuery.isLoading;
  const isError = detailQuery.isError;

  return (
    <div
      data-testid="notification-audit-drawer-root"
      className="fixed inset-0 z-50"
      role="presentation"
    >
      <div
        data-testid="notification-audit-drawer-backdrop"
        aria-hidden="true"
        onClick={onClose}
        className="fixed inset-0 bg-black/30"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="notification-audit-drawer-heading"
        onKeyDown={handlePanelKeyDown}
        className="fixed right-0 top-0 h-screen w-full lg:w-[480px] bg-ms-surface shadow-2xl border-l border-ms-border flex flex-col transform transition-transform duration-200 ease-out translate-x-0 overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-ms-border shrink-0">
          <h2
            id="notification-audit-drawer-heading"
            className="text-[15px] font-semibold text-ms-text-strong"
          >
            Dispatch attempt detail
          </h2>
          <Btn
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Close"
            data-testid="notification-audit-drawer-close"
          >
            <XMarkIcon className="w-4 h-4" />
          </Btn>
        </div>
        <div className="p-5 space-y-4">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          )}
          {isError && (
            <div className="text-rose-600 text-[12.5px]">
              Could not load audit row detail. Refresh to try again.
            </div>
          )}
          {detail && (
            <>
              <dl className="grid grid-cols-1 gap-y-2 text-[12.5px]">
                <div className="grid grid-cols-3 gap-2">
                  <dt className="text-ms-text-muted">State change ID</dt>
                  <dd className="col-span-2 font-mono text-ms-text-strong">
                    {detail.stateChangeId}
                  </dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="text-ms-text-muted">Project</dt>
                  <dd className="col-span-2">
                    {/* Phase 103 (D-01): projectId is nullable for
                        three project-less producer families (kudos,
                        training, bot-usage). Render "Global" instead of a
                        broken /projects/null link when absent — matching
                        NotificationsAuditPage's treatment. */}
                    {detail.projectId !== null ? (
                      <Link
                        to={`/projects/${detail.projectId}`}
                        className="text-ms-orange-dark hover:underline"
                      >
                        Project #{detail.projectId}
                      </Link>
                    ) : (
                      <span className="italic text-ms-text-muted">Global</span>
                    )}
                  </dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="text-ms-text-muted">Event kind</dt>
                  <dd className="col-span-2 text-ms-text-strong">
                    {(() => {
                      // We don't always know the event_kind from the audit row
                      // alone — the parent state_change carries it via
                      // `kind` / `eventKind` keys in the JSONB changes blob.
                      const payload = detail.stateChangePayload as
                        | { kind?: string; eventKind?: string }
                        | null;
                      const rawKind = payload?.eventKind ?? payload?.kind ?? null;
                      if (!rawKind) return <span>—</span>;
                      // Render via EventKindLabel when the kind value is a
                      // known taxonomy member; otherwise fall back to plain
                      // text so unknown values stay visible.
                      if (
                        (
                          [
                            'sla.warning',
                            'sla.breach',
                            'package.threshold_crossed',
                            'package.cap_blocked',
                            'package.zero_rate_overage',
                            'package.overage_threshold_crossed',
                            'package.overage_approved',
                          ] as readonly string[]
                        ).includes(rawKind)
                      ) {
                        return (
                          <EventKindLabel
                            kind={rawKind as Parameters<typeof EventKindLabel>[0]['kind']}
                          />
                        );
                      }
                      return <span>{rawKind}</span>;
                    })()}
                  </dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="text-ms-text-muted">Recipient</dt>
                  <dd className="col-span-2">
                    {detail.recipientId !== null ? (
                      <span className="text-ms-text-strong">
                        User #{detail.recipientId}
                      </span>
                    ) : (
                      <span className="italic text-ms-text-muted">
                        (channel target)
                      </span>
                    )}
                  </dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="text-ms-text-muted">Channel</dt>
                  <dd className="col-span-2 text-ms-text-strong">
                    <ChannelLabel channel={detail.channel} />
                  </dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="text-ms-text-muted">Target</dt>
                  <dd className="col-span-2 font-mono text-ms-text-strong break-all">
                    {detail.target ?? '—'}
                  </dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="text-ms-text-muted">Status</dt>
                  <dd className="col-span-2">
                    <StatusPill status={detail.status} />
                  </dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="text-ms-text-muted">Skip reason</dt>
                  <dd className="col-span-2 text-ms-text-strong">
                    {formatSkipReason(detail.skipReason)}
                  </dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="text-ms-text-muted">Dispatched at</dt>
                  <dd className="col-span-2 text-ms-text-strong">
                    {formatDisplayDateTime(detail.dispatchedAt)}
                  </dd>
                </div>
              </dl>
              <hr className="border-ms-border-subtle" />
              <div>
                <h3 className="text-[12px] font-semibold text-ms-text-strong mb-2">
                  Event payload
                </h3>
                <pre className="font-mono text-[11px] text-ms-text-secondary bg-ms-surface-2 p-3 rounded border border-ms-border-subtle overflow-x-auto">
                  {detail.stateChangePayload
                    ? JSON.stringify(detail.stateChangePayload, null, 2)
                    : 'No payload recorded'}
                </pre>
              </div>
              <hr className="border-ms-border-subtle" />
              <div>
                <h3 className="text-[12px] font-semibold text-ms-text-strong mb-2">
                  Error message
                </h3>
                {detail.errorMessage ? (
                  <pre className="font-mono text-[11px] text-rose-600 bg-rose-50 p-3 rounded border border-rose-200 overflow-x-auto whitespace-pre-wrap">
                    {detail.errorMessage}
                  </pre>
                ) : (
                  <p className="text-[12px] text-ms-text-muted italic">
                    No error recorded
                  </p>
                )}
              </div>
              {/* Hidden marker so a11y / regression tests can assert that the
                  focus-trap selector constant is wired in this component. */}
              <span
                data-testid="focusable-selector-marker"
                data-selector={FOCUSABLE_SELECTOR}
                className="sr-only"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
